import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Eye, EyeOff } from "lucide-react";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { adminApi, ApiError } from "@/lib/api";

export function LocalSignIn({ onComplete }: { onComplete: () => void }) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<1 | 2>(1);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [failed, setFailed] = useState(false);
  const [throttled, setThrottled] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number>();
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const shouldAutoSubmitRef = useRef(false);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const totpHintId = "local-sign-in-totp-hint";
  const totpErrorId = "local-sign-in-totp-error";

  useEffect(() => {
    if (stage === 2 && failed) totpInputRef.current?.focus();
  }, [failed, stage]);

  useEffect(() => {
    if (stage !== 2 || code.length !== 6 || !shouldAutoSubmitRef.current || busy) return;
    shouldAutoSubmitRef.current = false;
    totpInputRef.current?.form?.requestSubmit();
  }, [busy, code, stage]);

  function next(event: FormEvent) {
    event.preventDefault();
    setFailed(false);
    setThrottled(false);
    setRetryAfterSeconds(undefined);
    if (username.trim() && password) setStage(2);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (code.length !== 6 || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setFailed(false);
    setThrottled(false);
    setRetryAfterSeconds(undefined);
    try {
      await adminApi.login(username, password, code);
      setPassword("");
      setCode("");
      onComplete();
    } catch (error) {
      setCode("");
      setFailed(true);
      const isThrottled = error instanceof ApiError && error.status === 429;
      setThrottled(isThrottled);
      setRetryAfterSeconds(isThrottled ? error.retryAfterSeconds : undefined);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  }

  function updateCode(value: string) {
    shouldAutoSubmitRef.current = code.length !== 6 && value.length === 6;
    setCode(value);
    if (failed && !throttled) setFailed(false);
  }

  function back() {
    setPassword("");
    setCode("");
    setFailed(false);
    setThrottled(false);
    setRetryAfterSeconds(undefined);
    setStage(1);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10 text-ink">
      <section className="w-full max-w-md rounded-xl bg-panel p-5 shadow-panel sm:p-6">
        <header className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2.5 font-medium">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line bg-black text-sm font-semibold text-white" aria-hidden="true">
              L
            </span>
            <span className="truncate">License Management</span>
          </div>
          <p className="shrink-0 font-mono text-xs text-muted">
            {t("auth.step", { current: stage, total: 2, defaultValue: `Step ${stage} of 2` })}
          </p>
        </header>

        <div className="mt-8 space-y-2">
          <h1 className="text-2xl font-medium tracking-[-0.04em] sm:text-3xl">
            {stage === 1 ? t("auth.signInTitle", "Sign in to License Management") : t("auth.verifyTitle", "Verify your identity")}
          </h1>
          <p className="text-sm leading-6 text-muted">
            {stage === 1
              ? t("auth.localDescription", "Use your assigned local Admin Account.")
              : t("auth.verifyDescription", "Enter the 6-digit code from your authenticator app to start a new Admin Session.")}
          </p>
        </div>

        {stage === 1 ? (
          <form className="mt-7 space-y-4" onSubmit={next}>
            <label className="block space-y-1.5">
              <span className="text-sm">{t("auth.username", "Username")}</span>
              <Input autoFocus autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm">{t("auth.password", "Password")}</span>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setShowPassword((previous) => !previous)}
                  className="absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted transition-colors hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
                  aria-label={showPassword ? t("auth.hidePassword", "Hide password") : t("auth.showPassword", "Show password")}
                >
                  {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                </button>
              </div>
            </label>
            <Button variant="primary" size="lg" className="w-full" type="submit">
              {t("auth.continue", "Continue")}
            </Button>
          </form>
        ) : (
          <form className="mt-7 space-y-5" onSubmit={submit}>
            <div className="space-y-2">
              <Label className="block text-sm font-medium text-ink" htmlFor="local-sign-in-totp">
                {t("auth.totp", "Authentication code")}
              </Label>
              <InputOTP
                ref={totpInputRef}
                id="local-sign-in-totp"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
                value={code}
                onChange={updateCode}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }}
                disabled={busy}
                aria-invalid={failed}
                aria-describedby={failed ? `${totpHintId} ${totpErrorId}` : totpHintId}
                required
              >
                <InputOTPGroup className="mx-auto w-full max-w-[360px]">
                  {Array.from({ length: 6 }, (_, index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className={index === 3 ? "ml-1" : undefined}
                      aria-invalid={failed}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              <p id={totpHintId} className="text-xs leading-5 text-muted">
                {t("auth.totpHint", "Codes change every 30 seconds. You can paste the full code.")}
              </p>
            </div>

            {failed && (
              <div id={totpErrorId}>
                <InlineAlert
                  title={t("auth.failedTitle", "Could not sign in")}
                  message={
                    throttled
                      ? retryAfterSeconds === undefined
                        ? t("auth.throttledDescription", "Too many sign-in attempts. Wait and try again.")
                        : t("auth.throttledDescriptionWithWait", {
                            seconds: retryAfterSeconds,
                            defaultValue: "Too many sign-in attempts. Wait {{seconds}} seconds and try again.",
                          })
                      : t("auth.failedDescription", "Check your credentials and try again.")
                  }
                />
              </div>
            )}

            <div className="space-y-2.5">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                type="submit"
                disabled={busy || code.length !== 6}
                aria-busy={busy}
              >
                {busy ? <BusyLabel>{t("auth.signingIn", "Signing in…")}</BusyLabel> : t("auth.signIn", "Sign in")}
              </Button>
              <Button className="w-full" size="lg" type="button" variant="outline" onClick={back} disabled={busy}>
                {t("auth.back", "Back")}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-6 border-t border-line pt-4 text-xs leading-5 text-muted">
          {t("auth.sessionHint", "Every new session requires your password and authentication code.")}
        </p>
      </section>
    </main>
  );
}

export function Enrollment({ onComplete }: { onComplete: () => void }) {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [uri, setURI] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<"token" | "password" | "password-mismatch" | "totp" | "completion" | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      setURI(await adminApi.startEnrollment(token));
    } catch {
      setError("token");
    }
  }

  async function complete(event: FormEvent) {
    event.preventDefault();
    if (password.length < 12 || password.length > 256) {
      setError("password");
      return;
    }
    if (password !== confirmPassword) {
      setError("password-mismatch");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("totp");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApi.completeEnrollment(token, password, code);
      setToken("");
      setPassword("");
      setConfirmPassword("");
      setCode("");
      onComplete();
    } catch {
      setCode("");
      setError("completion");
    } finally {
      setBusy(false);
    }
  }

  const errorMessage =
    error === "token"
      ? t("auth.enrollmentTokenError", "Check the Enrollment Token and try again.")
      : error === "password"
        ? t("auth.passwordRequirementsError", "Use a password with 12 to 256 characters.")
        : error === "password-mismatch"
          ? t("auth.passwordMismatchError", "Make sure both passwords match and try again.")
          : error === "totp"
            ? t("auth.totpRequirementsError", "Enter the current 6-digit code from the authenticator that scanned this QR code.")
            : t("auth.enrollFailedDescription", "Use a current code from the authenticator that scanned this QR code. If it still fails, request a new Enrollment Token.");

  const errorTitle =
    error === "token"
      ? t("auth.enrollmentTokenErrorTitle", "Could not begin setup")
      : error === "password"
        ? t("auth.passwordRequirementsTitle", "Password does not meet requirements")
        : error === "password-mismatch"
          ? t("auth.passwordMismatchTitle", "Passwords do not match")
          : error === "totp"
            ? t("auth.totpRequirementsTitle", "Authentication code is incomplete")
            : t("auth.enrollFailedTitle", "Could not complete setup");

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 text-ink">
      <section className="w-full max-w-md rounded-xl bg-panel p-6 shadow-panel">
        <h1 className="text-2xl font-medium tracking-[-0.04em]">
          {t("auth.enrollTitle", "Set up Admin access")}
        </h1>
        <p className="mb-6 mt-1 text-sm text-muted">
          {t("auth.enrollDescription", "Use the one-time Enrollment Token issued to you.")}
        </p>
        {error && (
          <div className="mb-4">
            <InlineAlert title={errorTitle} message={errorMessage} />
          </div>
        )}
        {!uri ? (
          <form className="space-y-4" onSubmit={start}>
            <label className="block space-y-1.5">
              <span className="text-sm">{t("auth.enrollmentToken", "Enrollment Token")}</span>
              <Input autoFocus value={token} onChange={(event) => setToken(event.target.value)} required />
            </label>
            <Button variant="primary" className="w-full" type="submit">
              {t("auth.beginSetup", "Begin setup")}
            </Button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={complete}>
            <div className="space-y-3">
              <p className="text-sm text-muted">
                {t("auth.enrollScan", "Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the generated code.")}
              </p>
              <div className="flex justify-center border border-line bg-white p-3">
                <QRCodeSVG
                  value={uri}
                  size={224}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  className="h-auto max-w-full"
                  role="img"
                  aria-label={t("auth.setupQrCode", "Authenticator setup QR code")}
                />
              </div>
              <details className="text-xs text-muted">
                <summary className="cursor-pointer text-ink">
                  {t("auth.manualSetup", "Can't scan the QR code?")}
                </summary>
                <p className="mt-2 leading-5">
                  {t("auth.manualSetupDescription", "Open this setup link on a trusted device, then add it to an authenticator app. Do not share it.")}
                </p>
                <code className="mt-2 block break-all border border-line bg-canvas p-2 text-[11px] text-ink">
                  {uri}
                </code>
              </details>
              <p className="text-xs leading-5 text-muted">
                {t("auth.enrollQrSecurity", "This QR code contains a private setup secret. Do not share or screenshot it.")}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="block text-sm font-medium text-ink" htmlFor="enrollment-password">
                {t("auth.password", "Password")}
              </Label>
              <div className="relative">
                <Input
                  id="enrollment-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (error === "password" || error === "password-mismatch") setError(null);
                  }}
                  aria-invalid={error === "password" || error === "password-mismatch"}
                  aria-describedby="enrollment-password-requirements"
                  className="pr-10"
                  disabled={busy}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted transition-colors hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
                  aria-label={showPassword ? t("auth.hidePassword", "Hide password") : t("auth.showPassword", "Show password")}
                  disabled={busy}
                >
                  {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                </button>
              </div>
              <span id="enrollment-password-requirements" className="text-xs text-muted">
                {t("auth.passwordRequirements", "Use 12 to 256 characters.")}
              </span>
            </div>

            <div className="space-y-1.5">
              <Label className="block text-sm font-medium text-ink" htmlFor="enrollment-confirm-password">
                {t("auth.confirmPassword", "Confirm password")}
              </Label>
              <div className="relative">
                <Input
                  id="enrollment-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    if (error === "password-mismatch") setError(null);
                  }}
                  aria-invalid={error === "password-mismatch"}
                  className="pr-10"
                  disabled={busy}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted transition-colors hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
                  aria-label={showConfirmPassword ? t("auth.hideConfirmPassword", "Hide confirm password") : t("auth.showConfirmPassword", "Show confirm password")}
                  disabled={busy}
                >
                  {showConfirmPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="block text-sm font-medium text-ink" htmlFor="enrollment-totp">
                {t("auth.totp", "Authentication code")}
              </Label>
              <InputOTP
                id="enrollment-totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
                value={code}
                onChange={(value) => {
                  setCode(value);
                  if (error === "totp") setError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }}
                disabled={busy}
                aria-invalid={error === "totp"}
                required
              >
                <InputOTPGroup className="mx-auto w-full max-w-[360px]">
                  {Array.from({ length: 6 }, (_, index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className={index === 3 ? "ml-1" : undefined}
                      aria-invalid={error === "totp"}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button variant="primary" className="w-full" type="submit" disabled={busy}>
              {busy ? t("auth.completing", "Completing setup…") : t("auth.completeSetup", "Complete setup")}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}
